import { supabase } from '../lib/supabase';

/**
 * Brain — lecture des signaux.
 *
 * Huit vues en lecture seule, deja verrouillees EN BASE : chacune filtre sur
 * current_user_org_id() ET current_user_can_use_brain(). On n'ajoute donc
 * AUCUN filtre d'organisation ici — il serait redondant, et le dupliquer
 * inviterait a le desynchroniser un jour.
 *
 * Meme motif que denonciationsQuittancesService : client Supabase direct,
 * pas de react-query.
 */

const VIEWS = [
  'brain_signal_task_stall',
  'brain_signal_capture_gap',
  'brain_signal_extra_no_change_order',
  'brain_signal_unassigned_budget_line',
  'brain_signal_contract_unsigned',
  'brain_signal_contract_over_budget',
  'brain_signal_tm_budget_watch',
  'brain_signal_paid_unreleased',
  'brain_signal_invoice_approved_before_client_payment',
];

/**
 * Le theme est decide par le NOM DE LA VUE, pas par signal_code.
 *
 * signal_code n'est pas homogene d'une vue a l'autre : task_stall rend 'S7'
 * la ou d'autres rendent un nom semantique. S'y fier laissait les signaux
 * Terrain hors de toute carte — visibles nulle part. Le nom de la vue, lui,
 * est fixe et connu ici.
 */
export const THEME_BY_VIEW = {
  brain_signal_contract_over_budget: 'money',
  brain_signal_tm_budget_watch: 'money',
  brain_signal_contract_unsigned: 'legal',
  brain_signal_extra_no_change_order: 'legal',
  brain_signal_paid_unreleased: 'legal',
  brain_signal_invoice_approved_before_client_payment: 'money',
  brain_signal_unassigned_budget_line: 'engage',
  brain_signal_task_stall: 'field',
  brain_signal_capture_gap: 'field',
};

/**
 * COLONNES — trois piles verticales.
 *
 * Le signal_code decide, SAUF pour extra_no_change_order qui se scinde sur
 * son rule_code : un extra facture sans aucun avenant est de l'argent expose ;
 * un avenant qui existe mais qui dort est un document a faire avancer.
 *
 * Une seule table de correspondance : deplacer un signal d'une colonne a
 * l'autre reste une ligne a changer.
 */
export const COLUMNS = ['finances', 'legal', 'documents'];

export const COLUMN_BY_SIGNAL = {
  contract_over_budget: 'finances',
  tm_budget_watch: 'finances',
  contract_unsigned: 'legal',
  paid_unreleased: 'legal',
  // S17 : facture approuvee avant que le client ait paye sa DP.
  S17: 'finances',
  unassigned_budget_line: 'documents',
  capture_gap: 'documents',
  task_stall: 'documents',
};

export const COLUMN_BY_RULE = {
  invoiced_no_co: 'finances',
  invoiced_no_co_unapproved: 'finances',
  co_draft: 'documents',
  co_sent: 'documents',
  co_unsigned: 'documents',
};

export const columnOf = (s) =>
  COLUMN_BY_RULE?.[s?.rule_code] ??
  COLUMN_BY_SIGNAL?.[s?.signal_code] ??
  'documents';

/**
 * Correspondance variable i18n -> colonne de preuve, par vue.
 *
 * Les gabarits parlent en {value}, {pctMax}, {daysSite} ; les vues rendent
 * contract_value, pct_max, days_since_start. Sans cette table, les phrases
 * s'affichaient avec leurs accolades en clair.
 *
 * Le second element dit COMMENT presenter la valeur :
 *   money  -> formate en devise      num -> tel quel
 *   date   -> formate en date        text -> tel quel
 */
export const VARS_BY_VIEW = {
  brain_signal_task_stall: {
    task: ['task_description', 'text'],
    pct: ['current_pourcentage', 'num'],
    days: ['days_since_last_movement', 'num'],
    company: ['subcontractor_name', 'text'],
    logs: ['consecutive_logs_unchanged', 'num'],
    date: ['last_movement_log_date', 'date'],
  },
  brain_signal_capture_gap: {
    expected: ['expected_working_days', 'num'],
    logs: ['log_count_total', 'num'],
    pct: ['coverage_pct', 'num'],
    missing: ['missing_working_days', 'num'],
  },
  brain_signal_extra_no_change_order: {
    amount: ['invoiced_approved_ttc', 'money'],
  },
  brain_signal_unassigned_budget_line: {
    poste: ['budget_description', 'text'],
    budget: ['budget_amount', 'money'],
    days: ['days_since_start', 'num'],
    daysUntil: ['days_until_start', 'num'],
  },
  brain_signal_contract_unsigned: {
    company: ['company', 'text'],
    daysContract: ['days_since_created', 'num'],
    daysSite: ['days_since_start', 'num'],
  },
  brain_signal_contract_over_budget: {
    company: ['company', 'text'],
    value: ['contract_value', 'money'],
    budget: ['budget_amount', 'money'],
    pct: ['pct_of_budget', 'num'],
    overage: ['overage_amount', 'money'],
  },
  brain_signal_tm_budget_watch: {
    companies: ['tm_companies', 'text'],
    poste: ['poste', 'text'],
    budget: ['budget_amount', 'money'],
    pctMin: ['pct_min', 'num'],
    pctMax: ['pct_max', 'num'],
  },
  brain_signal_paid_unreleased: {
    company: ['subcontractor_name', 'text'],
    uncovered: ['uncovered', 'money'],
    released: ['released_by_subcontractor', 'money'],
  },
  brain_signal_invoice_approved_before_client_payment: {
    invoice: ['invoice_number', 'text'],
    dp: ['payment_request_number', 'num'],
    status: ['payment_request_status', 'dpStatus'],
    amount: ['total_amount', 'money'],
  },
};

/**
 * Le montant qui compte comme EXPOSITION, par vue. Pas n'importe quel montant :
 * le depassement pour un contrat hors budget, le reste a depenser pour une
 * regie — jamais le budget total, qui n'est pas un risque.
 * Les deux vues Terrain n'en portent aucun : elles rendent null.
 */
export const AMOUNT_BY_VIEW = {
  brain_signal_contract_over_budget: 'overage_amount',
  brain_signal_tm_budget_watch: 'remaining_worst_case',
  brain_signal_extra_no_change_order: 'invoiced_approved_ttc',
  brain_signal_unassigned_budget_line: 'budget_amount',
  brain_signal_contract_unsigned: 'contract_value',
  // Ce qui est sorti sans contrepartie signee — pas le paye total, qui n'est
  // pas un risque en soi.
  brain_signal_paid_unreleased: 'uncovered',
  brain_signal_invoice_approved_before_client_payment: 'total_amount',
};

/**
 * Exception PAR REGLE, quand la vue seule ne suffit plus.
 *
 * released_without_payment decrit une quittance signee alors que RIEN n'a ete
 * verse : `uncovered` y vaut donc zero par construction, et la carte affichait
 * 0,00 $ sous une phrase qui annoncait le montant quittance. Le chiffre qui
 * porte le risque, ici, c'est ce qui a ete decharge.
 *
 * Ne vaut que pour cette regle. Toutes les autres, y compris les quatre soeurs
 * de paid_unreleased, gardent le montant de leur vue.
 */
export const AMOUNT_BY_RULE = {
  released_without_payment: 'released_by_subcontractor',
};

/** Ce qui NOMME l'entite : sans lui, neuf lignes identiques a l'ecran. */
export const LABEL_BY_VIEW = {
  brain_signal_task_stall: 'task_description',
  brain_signal_capture_gap: 'project_name',
  brain_signal_extra_no_change_order: 'description',
  brain_signal_unassigned_budget_line: 'budget_description',
  brain_signal_contract_unsigned: 'company',
  brain_signal_contract_over_budget: 'company',
  brain_signal_tm_budget_watch: 'poste',
  brain_signal_paid_unreleased: 'subcontractor_name',
  brain_signal_invoice_approved_before_client_payment: 'invoice_number',
};

export const THEMES = ['money', 'legal', 'engage', 'field'];

// Ordre d'urgence. 'none' n'apparait pas : ces lignes sont ecartees des cartes.
const SEVERITY_RANK = { red: 0, amber: 1, yellow: 2, none: 3 };

// Les dix colonnes communes aux huit vues. Tout le reste est une PREUVE,
// propre a la vue, et sert de variables d'interpolation a la phrase i18n.
const COMMON = [
  'organization_id', 'project_id', 'entity_type', 'entity_id', 'signal_code',
  'can_assess', 'reason_code', 'severity', 'rule_code', 'confidence',
];

const toSignal = (row, view) => {
  const evidence = {};
  for (const [k, v] of Object.entries(row || {})) {
    if (!COMMON?.includes(k)) evidence[k] = v;
  }
  return {
    // Identifiant stable pour React : une entite peut porter plusieurs
    // signaux, et une vue peut voir la meme entite qu'une autre.
    key: `${view}::${row?.entity_id}::${row?.rule_code}`,
    view,
    organization_id: row?.organization_id,
    project_id: row?.project_id,
    entity_type: row?.entity_type,
    entity_id: row?.entity_id,
    signal_code: row?.signal_code,
    can_assess: row?.can_assess,
    reason_code: row?.reason_code,
    severity: row?.severity,
    rule_code: row?.rule_code,
    confidence: row?.confidence,
    theme: THEME_BY_VIEW?.[view] || null,
    column: null,   // pose apres construction, il depend de rule_code
    // Montant expose, deja extrait : la page n'a pas a deviner la colonne.
    amount: (() => {
      // La regle prime sur la vue ; sans exception, la vue decide.
      const col = AMOUNT_BY_RULE?.[row?.rule_code] ?? AMOUNT_BY_VIEW?.[view];
      const v = col ? row?.[col] : null;
      return v != null && !isNaN(parseFloat(v)) ? parseFloat(v) : null;
    })(),
    label: row?.[LABEL_BY_VIEW?.[view]] ?? null,
    evidence,
  };
};

/**
 * Charge les huit vues EN PARALLELE et rend une liste plate.
 *
 * Une vue indisponible ne vide pas la page : elle rend zero ligne et les sept
 * autres s'affichent. Meme precaution que pour la table quittances.
 */
export async function getBrainSignals() {
  const results = await Promise.all(
    VIEWS?.map(async (view) => {
      try {
        const { data, error } = await supabase?.from(view)?.select('*');
        if (error) throw error;
        return (data || [])?.map((row) => toSignal(row, view));
      } catch (err) {
        console.error(`[brainService] ${view} unavailable:`, err?.message);
        return [];
      }
    })
  );

  const all = results?.flat()?.map((s) => ({ ...s, column: columnOf(s) }));

  // Ce que Brain NE PEUT PAS evaluer. Ces lignes ne sont pas cachees : ne pas
  // savoir est une information, et la taire ferait croire a une absence de
  // probleme.
  const blind = all?.filter((s) => s?.can_assess === false);

  // Les cartes ne montrent que ce qui merite un regard : evaluable, et
  // au-dessus de 'none'.
  const signals = all
    ?.filter((s) => s?.can_assess !== false && s?.severity !== 'none')
    ?.sort(
      (a, b) =>
        (SEVERITY_RANK?.[a?.severity] ?? 9) - (SEVERITY_RANK?.[b?.severity] ?? 9)
    );

  return { signals, blind, all };
}

export default { getBrainSignals, COLUMNS, columnOf, THEME_BY_VIEW, VARS_BY_VIEW, AMOUNT_BY_VIEW, LABEL_BY_VIEW, THEMES };
